import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Sword, ArrowLeft, Trophy, Star, Heart,
  RotateCcw, Shield, Sparkles, Scroll
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const WordBuilderGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [difficulty, setDifficulty] = useState('medium');
  const [score, setScore] = useState(0);
  const [level, setLevel] = useState(1);
  const [health, setHealth] = useState(100);
  const [currentWord, setCurrentWord] = useState(null);
  const [revealedLetters, setRevealedLetters] = useState([]);
  const [availableLetters, setAvailableLetters] = useState([]);
  const [selectedLetters, setSelectedLetters] = useState([]);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [combo, setCombo] = useState(0);
  const [showDamage, setShowDamage] = useState(false);
  const [xp, setXp] = useState(0);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [maxHints, setMaxHints] = useState(3);

  // Difficulty settings
  const difficultySettings = {
    easy: { health: 150, damage: 15, extraLetters: 1, levelThreshold: 4, startLevel: 1 },
    medium: { health: 100, damage: 20, extraLetters: 3, levelThreshold: 3, startLevel: 1 },
    hard: { health: 75, damage: 30, extraLetters: 5, levelThreshold: 2, startLevel: 2 }
  };

  // Word lists by difficulty
  const wordLists = {
    1: ['cat', 'dog', 'sun', 'hat', 'run', 'big', 'red', 'pen', 'cup', 'box'],
    2: ['book', 'tree', 'fish', 'bird', 'cake', 'play', 'jump', 'swim', 'dark', 'lamp'],
    3: ['house', 'water', 'plant', 'happy', 'cloud', 'dream', 'lunch', 'beach', 'chair', 'phone'],
    4: ['garden', 'school', 'family', 'friend', 'animal', 'travel', 'winter', 'summer', 'bridge', 'castle'],
    5: ['rainbow', 'monster', 'kitchen', 'excited', 'mystery', 'treasure', 'captain', 'holiday', 'champion', 'adventure']
  };

  const generateWord = useCallback(() => {
    const words = wordLists[Math.min(level, 5)];
    const word = words[Math.floor(Math.random() * words.length)].toUpperCase();
    const settings = difficultySettings[difficulty];
    
    // Create scrambled letters with extra letters based on difficulty
    const letters = word.split('');
    const extraLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('')
      .filter(l => !letters.includes(l))
      .sort(() => Math.random() - 0.5)
      .slice(0, Math.min(settings.extraLetters, level + settings.extraLetters));
    
    const allLetters = [...letters, ...extraLetters].sort(() => Math.random() - 0.5);
    
    // Give hint - reveal first letter
    const hint = word[0];
    
    return {
      word,
      hint,
      letters: allLetters,
      points: word.length * 10 + (level * 5)
    };
  }, [level, difficulty]);

  const startGame = useCallback((selectedDifficulty) => {
    if (selectedDifficulty) setDifficulty(selectedDifficulty);
    const settings = difficultySettings[selectedDifficulty || difficulty];
    setGameState('playing');
    setScore(0);
    setLevel(settings.startLevel);
    setHealth(settings.health);
    setWordsCompleted(0);
    setCombo(0);
    setXp(0);
    const newWord = generateWord();
    setCurrentWord(newWord);
    setAvailableLetters(newWord.letters.map((l, i) => ({ letter: l, id: i, used: false })));
    setSelectedLetters([]);
    setRevealedLetters([newWord.hint]);
  }, [generateWord, difficulty]);

  // Select letter
  const selectLetter = (letterObj) => {
    if (letterObj.used) return;
    
    setAvailableLetters(prev => 
      prev.map(l => l.id === letterObj.id ? { ...l, used: true } : l)
    );
    setSelectedLetters(prev => [...prev, letterObj]);
  };

  // Remove letter
  const removeLetter = (index) => {
    const letterToRemove = selectedLetters[index];
    setSelectedLetters(prev => prev.filter((_, i) => i !== index));
    setAvailableLetters(prev =>
      prev.map(l => l.id === letterToRemove.id ? { ...l, used: false } : l)
    );
  };

  // Submit word
  const submitWord = () => {
    const builtWord = selectedLetters.map(l => l.letter).join('');
    
    if (builtWord === currentWord.word) {
      // Correct!
      const bonusPoints = combo * 5;
      const totalPoints = currentWord.points + bonusPoints;
      setScore(s => s + totalPoints);
      setCombo(c => c + 1);
      setWordsCompleted(w => w + 1);
      setXp(x => x + 20);
      
      toast.success(
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-amber-400" />
          <span>+{totalPoints} XP! {combo > 0 && `${combo}x Combo!`}</span>
        </div>
      );
      
      // Level up based on difficulty threshold
      const settings = difficultySettings[difficulty];
      if ((wordsCompleted + 1) % settings.levelThreshold === 0) {
        setLevel(l => Math.min(l + 1, 5));
        toast.success(`Level Up! Now Level ${Math.min(level + 1, 5)}!`);
      }
      
      // New word
      const newWord = generateWord();
      setCurrentWord(newWord);
      setAvailableLetters(newWord.letters.map((l, i) => ({ letter: l, id: i, used: false })));
      setSelectedLetters([]);
      setRevealedLetters([newWord.hint]);
    } else {
      // Wrong!
      const settings = difficultySettings[difficulty];
      setShowDamage(true);
      setTimeout(() => setShowDamage(false), 500);
      setCombo(0);
      setHealth(h => {
        const newHealth = h - settings.damage;
        if (newHealth <= 0) {
          setGameState('finished');
        }
        return Math.max(0, newHealth);
      });
      toast.error(`Wrong word! -${settings.damage} HP`);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setAvailableLetters(prev => prev.map(l => ({ ...l, used: false })));
    setSelectedLetters([]);
  };

  // Use hint - reveal next letter
  const useHint = () => {
    if (hintsUsed >= maxHints || !currentWord) return;
    
    const currentLength = selectedLetters.length;
    if (currentLength >= currentWord.word.length) return;
    
    // Find the next correct letter
    const nextLetter = currentWord.word[currentLength];
    const letterToSelect = availableLetters.find(l => l.letter === nextLetter && !l.used);
    
    if (letterToSelect) {
      selectLetter(letterToSelect);
      setHintsUsed(h => h + 1);
      toast.info(`Hint used! ${maxHints - hintsUsed - 1} hints remaining`);
    }
  };

  // Give up - skip this word
  const giveUp = () => {
    toast.info(`The word was: ${currentWord.word}`);
    setCombo(0);
    
    // Small health penalty for giving up
    const settings = difficultySettings[difficulty];
    setHealth(h => {
      const newHealth = h - Math.floor(settings.damage / 2);
      if (newHealth <= 0) {
        setGameState('finished');
      }
      return Math.max(0, newHealth);
    });
    
    // Move to next word
    const newWord = generateWord();
    setCurrentWord(newWord);
    setAvailableLetters(newWord.letters.map((l, i) => ({ letter: l, id: i, used: false })));
    setSelectedLetters([]);
    setRevealedLetters([newWord.hint]);
  };

  // End game
  useEffect(() => {
    if (gameState === 'finished') {
      const saveGame = async () => {
        try {
          await api.post('/games/complete', {
            game_id: 'word-builder',
            score: score,
            time_seconds: 0,
            metadata: { level, words_completed: wordsCompleted, max_combo: combo }
          });
        } catch (error) {
          console.error('Error saving game:', error);
        }
      };
      saveGame();
    }
  }, [gameState, score, level, wordsCompleted, combo, api]);

  const getRank = () => {
    if (wordsCompleted >= 15) return { name: 'Word Master', color: 'text-amber-400' };
    if (wordsCompleted >= 10) return { name: 'Word Knight', color: 'text-purple-400' };
    if (wordsCompleted >= 5) return { name: 'Word Squire', color: 'text-blue-400' };
    return { name: 'Word Apprentice', color: 'text-emerald-400' };
  };

  return (
    <StudentLayout>
      <div data-testid="word-builder-game" className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
          
          {gameState === 'playing' && (
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Heart className="w-5 h-5 text-red-500" />
                <div className="w-24 h-3 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className={`h-full transition-all ${health > 50 ? 'bg-emerald-500' : health > 25 ? 'bg-amber-500' : 'bg-red-500'}`}
                    style={{ width: `${health}%` }}
                  />
                </div>
              </div>
              <div className="flex items-center gap-2 bg-amber-100 px-3 py-1 rounded-full">
                <Star className="w-4 h-4 text-amber-600" />
                <span className="font-bold text-amber-700">{score}</span>
              </div>
            </div>
          )}
        </div>

        {/* Game Title */}
        <div className="student-card bg-gradient-to-r from-violet-100 to-purple-100 border-purple-300 mb-4">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-purple-500 rounded-2xl border-2 border-gray-800 flex items-center justify-center">
              <Sword className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold text-slate-800">Word Builder Quest</h1>
              <p className="text-slate-600">
                {gameState === 'playing' ? `Level ${level} Knight • ${combo}x Combo` : 'Build words, gain XP!'}
              </p>
            </div>
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-purple-900 to-violet-900">
            <div className="w-24 h-24 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <Sword className="w-14 h-14 text-white animate-pulse" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Word Builder Quest</h2>
            <p className="text-lg text-purple-200 mb-6">
              Arrange letters to form words and level up your character!
            </p>
            
            {/* Difficulty Selection */}
            <div className="mb-8">
              <p className="text-purple-300 mb-4">Select Difficulty:</p>
              <div className="flex justify-center gap-3">
                <button
                  onClick={() => setDifficulty('easy')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'easy' 
                      ? 'bg-green-500 text-white scale-105' 
                      : 'bg-purple-800 text-purple-300 hover:bg-purple-700'
                  }`}
                >
                  Easy
                  <span className="block text-xs opacity-75">150 HP, few extras</span>
                </button>
                <button
                  onClick={() => setDifficulty('medium')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'medium' 
                      ? 'bg-amber-500 text-white scale-105' 
                      : 'bg-purple-800 text-purple-300 hover:bg-purple-700'
                  }`}
                >
                  Medium
                  <span className="block text-xs opacity-75">100 HP, normal</span>
                </button>
                <button
                  onClick={() => setDifficulty('hard')}
                  className={`px-6 py-3 rounded-xl font-bold transition-all ${
                    difficulty === 'hard' 
                      ? 'bg-red-500 text-white scale-105' 
                      : 'bg-purple-800 text-purple-300 hover:bg-purple-700'
                  }`}
                >
                  Hard
                  <span className="block text-xs opacity-75">75 HP, many extras</span>
                </button>
              </div>
            </div>
            
            <div className="text-purple-300 mb-8 space-y-1">
              <p>🗡️ Build correct words to earn XP</p>
              <p>💰 Chain combos for bonus points</p>
              <p>❤️ Wrong answers damage your health</p>
            </div>
            <Button onClick={() => startGame(difficulty)} className="bg-purple-500 hover:bg-purple-600 text-white text-lg px-10 py-6">
              Begin Quest
              <Scroll className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentWord && (
          <div className={`student-card bg-gradient-to-b from-purple-900 to-violet-900 min-h-[450px] transition-all ${showDamage ? 'animate-shake bg-red-900' : ''}`}>
            {/* Quest Info */}
            <div className="flex justify-between items-center mb-6">
              <div className="text-purple-200">
                <span className="text-xs">LEVEL {level}</span>
                <p className={`font-bold ${getRank().color}`}>{getRank().name}</p>
              </div>
              <div className="text-center">
                <span className="text-purple-300 text-sm">Words: {wordsCompleted}</span>
              </div>
              {combo > 1 && (
                <div className="bg-amber-500/20 px-3 py-1 rounded-full">
                  <span className="text-amber-300 font-bold">{combo}x COMBO!</span>
                </div>
              )}
            </div>

            {/* Hint */}
            <div className="text-center mb-6">
              <p className="text-purple-300 text-sm mb-2">Build a {currentWord.word.length}-letter word</p>
              <div className="flex justify-center gap-2">
                {currentWord.word.split('').map((_, i) => (
                  <div key={i} className="w-10 h-12 bg-purple-800/50 rounded border-2 border-purple-500 flex items-center justify-center">
                    {i === 0 && (
                      <span className="text-xl font-bold text-purple-300">{currentWord.hint}</span>
                    )}
                  </div>
                ))}
              </div>
              <p className="text-purple-400 text-xs mt-2">Hint: Starts with "{currentWord.hint}"</p>
            </div>

            {/* Selected Letters (Answer) */}
            <div className="mb-6">
              <p className="text-purple-300 text-sm mb-2 text-center">Your Word:</p>
              <div className="flex justify-center gap-2 min-h-[60px] bg-purple-800/30 rounded-lg p-3">
                {selectedLetters.map((letterObj, i) => (
                  <button
                    key={i}
                    onClick={() => removeLetter(i)}
                    className="w-12 h-12 bg-purple-500 rounded-lg border-2 border-purple-300 flex items-center justify-center hover:bg-purple-400 transition-all transform hover:scale-105"
                  >
                    <span className="text-xl font-bold text-white">{letterObj.letter}</span>
                  </button>
                ))}
                {selectedLetters.length === 0 && (
                  <span className="text-purple-500 self-center">Click letters below...</span>
                )}
              </div>
            </div>

            {/* Available Letters */}
            <div className="mb-6">
              <p className="text-purple-300 text-sm mb-2 text-center">Available Letters:</p>
              <div className="flex justify-center gap-2 flex-wrap">
                {availableLetters.map((letterObj) => (
                  <button
                    key={letterObj.id}
                    onClick={() => selectLetter(letterObj)}
                    disabled={letterObj.used}
                    className={`w-12 h-12 rounded-lg border-2 flex items-center justify-center transition-all transform ${
                      letterObj.used 
                        ? 'bg-purple-900/50 border-purple-800 opacity-50 cursor-not-allowed' 
                        : 'bg-purple-700 border-purple-400 hover:bg-purple-600 hover:scale-105'
                    }`}
                  >
                    <span className={`text-xl font-bold ${letterObj.used ? 'text-purple-700' : 'text-white'}`}>
                      {letterObj.letter}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-3 flex-wrap">
              <Button 
                variant="outline" 
                onClick={clearSelection}
                className="border-purple-400 text-purple-200 hover:bg-purple-800"
              >
                Clear
              </Button>
              <Button 
                onClick={useHint}
                disabled={hintsUsed >= maxHints}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                💡 Hint ({maxHints - hintsUsed})
              </Button>
              <Button 
                onClick={giveUp}
                variant="outline"
                className="border-red-400 text-red-300 hover:bg-red-900"
              >
                Skip Word
              </Button>
              <Button 
                onClick={submitWord}
                disabled={selectedLetters.length !== currentWord.word.length}
                className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
              >
                <Shield className="w-4 h-4 mr-2" />
                Submit Word
              </Button>
            </div>
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card text-center py-12 bg-gradient-to-b from-purple-900 to-violet-900">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Quest Complete!</h2>
            <p className={`text-xl mb-4 ${getRank().color}`}>{getRank().name}</p>
            
            <div className="grid grid-cols-2 gap-4 my-8 max-w-xs mx-auto">
              <div className="bg-purple-800/50 rounded-xl p-4">
                <p className="text-3xl font-bold text-emerald-400">{wordsCompleted}</p>
                <p className="text-sm text-purple-300">Words</p>
              </div>
              <div className="bg-purple-800/50 rounded-xl p-4">
                <p className="text-3xl font-bold text-purple-300">{level}</p>
                <p className="text-sm text-purple-300">Level</p>
              </div>
            </div>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-400">+{score}</span>
              <span className="text-purple-200 text-xl">XP</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={() => startGame()} className="border-purple-400 text-purple-200 hover:bg-purple-800">
                <RotateCcw className="w-4 h-4 mr-2" />
                New Quest
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-purple-500 hover:bg-purple-600">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default WordBuilderGame;
